import { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CheckInDateMode } from '@/lib/checkin-date';

const MODES: { value: CheckInDateMode; label: string }[] = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'semana', label: 'Semana' },
  { value: 'mes', label: 'Mes' },
  { value: 'intervalo', label: 'Intervalo' },
];

interface CheckInDateFilterProps {
  mode: CheckInDateMode;
  onModeChange: (mode: CheckInDateMode) => void;
  start: string;
  end: string;
  onStartChange: (date: string) => void;
  onEndChange: (date: string) => void;
}

export function CheckInDateFilter({
  mode,
  onModeChange,
  start,
  end,
  onStartChange,
  onEndChange,
}: CheckInDateFilterProps) {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const formatDisplay = (dateStr: string) => {
    if (!dateStr) return 'Seleccionar';
    return format(new Date(dateStr + 'T00:00:00'), "d MMM, yy", { locale: es });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <div className="flex items-center rounded-lg border bg-muted/30 p-0.5">
        {MODES.map((m) => (
          <Button
            key={m.value}
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              'h-7 px-3 text-[11px] font-bold uppercase tracking-wide',
              mode === m.value
                ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => onModeChange(m.value)}
          >
            {m.label}
          </Button>
        ))}
      </div>

      {mode === 'intervalo' && (
        <div className="flex items-center gap-2">
          <Popover open={startOpen} onOpenChange={setStartOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'h-8 justify-start text-left font-normal text-xs gap-2 min-w-[130px]',
                  !start && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="h-3.5 w-3.5" />
                {start ? formatDisplay(start) : 'Fecha inicio'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={start ? new Date(start + 'T00:00:00') : undefined}
                onSelect={(date) => {
                  if (date) {
                    onStartChange(format(date, 'yyyy-MM-dd'));
                    setStartOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <span className="text-xs text-muted-foreground">→</span>

          <Popover open={endOpen} onOpenChange={setEndOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'h-8 justify-start text-left font-normal text-xs gap-2 min-w-[130px]',
                  !end && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="h-3.5 w-3.5" />
                {end ? formatDisplay(end) : 'Fecha fin'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={end ? new Date(end + 'T00:00:00') : undefined}
                onSelect={(date) => {
                  if (date) {
                    onEndChange(format(date, 'yyyy-MM-dd'));
                    setEndOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
